import React from 'react';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { ManualTag } from '../contact-management.types';
import TagItem from './tag-item';

interface Props {
  tags: ManualTag[];
  tagsLoading: boolean;
  page: number;
  rowsPerPage: number;
  total: number;

  handleChangePage(event: unknown, newPage: number): void;
  handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): void;
  handleOpenEditTagDialog(tag: ManualTag): () => void;
  handleOpenDeleteTagDialog(tag: ManualTag): () => void;
}

const TagTable: React.FC<Props> = ({
  tags,
  tagsLoading,
  page,
  rowsPerPage,
  total,
  handleChangePage,
  handleChangeRowsPerPage,
  handleOpenEditTagDialog,
  handleOpenDeleteTagDialog
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader className={classes.table}>
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('createDate')}</TableCell>
              {/* One extra cell for the menu icon */}
              <TableCell className={classes.actionCell} />
            </TableRow>
          </TableHead>

          <TableBody>
            {tagsLoading ? (
              // If loading, show skeleton rows
              (Array.from({ length: rowsPerPage }, (_, idx1) => (
                <TableRow key={idx1} hover>
                  {Array.from({ length: 3 }, (_, idx2) => (
                    <TableCell key={idx2} className={classes.cell}>
                      <Skeleton variant="rectangular" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              )))
            ) : !_.isEmpty(tags) ? (
              // If we have tags, map over them
              (tags.map((tag) => (
                <TagItem
                  key={tag._id}
                  tag={tag}
                  handleOpenEditTagDialog={handleOpenEditTagDialog(tag)}
                  handleOpenDeleteTagDialog={handleOpenDeleteTagDialog(tag)}
                />
              )))
            ) : (
              // If no tags found
              (<TableRow hover>
                <TableCell colSpan={3} className={classes.noItemsCell}>
                  {t('noTagsFound')}
                </TableCell>
              </TableRow>)
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t('rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('of')} ${count}`}
        // hide pagination if there are no items
        sx={{ visibility: total === 0 ? 'hidden' : 'visible' }}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    width: '100%',
    // Provide a specific max-height if you want a scrollable area
    maxHeight: 'calc(100% - 32px - 16px - 16px - 52px)',
    backgroundColor: '#FFF',
    border: 'none',
    borderRadius: '8px',
    boxShadow: 'none',
  },
  table: {
    borderCollapse: 'separate',
    // Remove default MUI table borders
    '& th, & td': {
      border: 'none',
    },
    '& th': {
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 600,
      backgroundColor: '#FFF',
      borderBottom: `1px solid ${theme.palette.greys?.light || '#ddd'}`,
      padding: '8px 24px',
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
      },
    },
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9,
  },
  cell: {
    fontSize: theme.typography.pxToRem(13),
    padding: '8px 24px',
    borderBottom: `1px solid ${theme.palette.greys?.light || '#ddd'}`,
    verticalAlign: 'middle',
  },
  actionCell: {
    width: '44px',
    borderBottom: `1px solid ${theme.palette.greys?.light || '#ddd'}`,
  },
  noItemsCell: {
    fontSize: theme.typography.pxToRem(13),
    textAlign: 'center',
    padding: '16px',
  },
}));

export default TagTable;
